/**
 * PageTransition Component
 *
 * Provides smooth fade/slide animations when navigating between routes
 * Enhances user experience with subtle transitions
 *
 * Features:
 * - Fade animation with configurable duration
 * - Optional slide effect (up/down)
 * - Accessible (respects prefers-reduced-motion)
 * - Lightweight wrapper component
 */

import React, { ReactNode } from 'react'
import { Box } from '@mui/material'
import { useLocation } from 'react-router-dom'

export interface PageTransitionProps {
  /** Content to animate */
  children: ReactNode
  /** Animation duration in milliseconds */
  duration?: number
  /** Enable slide effect in addition to fade */
  enableSlide?: boolean
  /** Slide direction */
  slideDirection?: 'up' | 'down'
  /** Optional test ID */
  testId?: string
}

/**
 * PageTransition Component
 *
 * Wraps page content with fade/slide animation on route changes
 */
export const PageTransition: React.FC<PageTransitionProps> = ({
  children,
  duration = 300,
  enableSlide = true,
  slideDirection = 'up',
  testId = 'page-transition',
}) => {
  const location = useLocation()

  // Calculate transform based on slide direction
  const slideTransform =
    slideDirection === 'up' ? 'translateY(20px)' : 'translateY(-20px)'

  return (
    <Box
      key={location.pathname} // Re-mount on route change
      data-testid={testId}
      sx={{
        animation: `fadeSlideIn ${duration}ms ease-out`,
        '@keyframes fadeSlideIn': {
          from: {
            opacity: 0,
            transform: enableSlide ? slideTransform : 'none',
          },
          to: {
            opacity: 1,
            transform: 'translateY(0)',
          },
        },
        // Respect user's motion preferences
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
          transform: 'none',
        },
      }}
    >
      {children}
    </Box>
  )
}

export default PageTransition
